import { useMemo } from "react";
import type { TComment, THierarchicalComment } from "./typings";
import { I18n } from "@/services/i18n";
import { isEmpty } from "@/utils/is-empty";

const useNormalizedComments = (
  comments: TComment[],
): THierarchicalComment[] => {
  return useMemo(() => {
    if (isEmpty(comments)) return [];
    return comments.map((comment) => ({
      ...comment,
      children: [],
    }));
  }, [comments]);
};
export const useHierarchicalComments = (
  rawComments: TComment[],
): THierarchicalComment[] => {
  const comments = useNormalizedComments(rawComments);
  return useMemo(() => {
    if (isEmpty(comments)) return [];
    const findParent = (
      comments: THierarchicalComment[] | null,
      parent: number,
    ): THierarchicalComment | null => {
      if (!comments || isEmpty(comments)) {
        return null;
      }
      const [head, ...tail] = comments;
      return head.id === parent
        ? head
        : findParent([...(head.children || []), ...tail], parent);
    };
    const len = comments.length - 1;
    let current: THierarchicalComment;
    for (let i = len; i >= 0; i--) {
      current = comments[i];
      if (current.parentCommentId) {
        for (let j = 0; j <= len; j++) {
          if (j === i) continue;
          const parent = findParent([comments[j]], current.parentCommentId);
          if (parent?.children) {
            parent.children.unshift(current);
            comments.splice(i, 1);
            break;
          }
        }
      }
    }
    return comments;
  }, [comments]);
};

export const useAuthorAvatar = (comment: TComment) => {
  return (
    comment.author?.avatarUrl ??
    "https://secure.gravatar.com/avatar/a0ada0cde61a3b2a475c592cd5f71393?s=96&d=mm&r=g"
  );
};
export const useCommentsCount = (comments: TComment[]) => {
  return I18n.pluralize(comments?.length || 0, [
    "комментарий",
    "комментария",
    "комментариев",
  ]);
};
